import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { MessageSquare, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function ChatSettings({ getAuthHeader }) {
  const [repliesText, setRepliesText] = useState('');
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/chat-settings`, getAuthHeader());
      setRepliesText(res.data.quick_replies.join('\n'));
      setHours(res.data.office_hours);
    } catch { toast.error('Failed to load chat settings'); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/chat-settings`, {
        quick_replies: repliesText.split('\n').map(s => s.trim()).filter(Boolean),
        office_hours: hours,
      }, getAuthHeader());
      toast.success('Chat settings saved — staff chips and client away note updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleDay = (d) => {
    const days = hours.days.includes(d) ? hours.days.filter(x => x !== d) : [...hours.days, d].sort();
    setHours({ ...hours, days });
  };

  if (!hours) return null;

  return (
    <Card className="border-l-4 border-l-blue-500" data-testid="chat-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Live Chat Settings</CardTitle>
        <p className="text-xs text-muted-foreground">Canned quick replies for staff and office hours shown to clients</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-sm font-medium">Quick Replies (one per line, max 20)</Label>
          <Textarea rows={7} value={repliesText} onChange={e => setRepliesText(e.target.value)}
            className="mt-1.5 text-sm" data-testid="quick-replies-textarea"
            placeholder={'Your loan has been approved 🎉\nPlease sign your loan documents...'} />
          <p className="text-[11px] text-slate-400 mt-1">These appear as one-tap chips above the chat reply box for all staff.</p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4" /> Office Hours (away note outside these times)</Label>
            <Switch checked={hours.enabled} onCheckedChange={v => setHours({ ...hours, enabled: v })} data-testid="office-hours-switch" />
          </div>
          {hours.enabled && (
            <>
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input type="time" value={hours.start} onChange={e => setHours({ ...hours, start: e.target.value })} className="w-32" data-testid="office-start-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input type="time" value={hours.end} onChange={e => setHours({ ...hours, end: e.target.value })} className="w-32" data-testid="office-end-input" />
                </div>
                <p className="text-[11px] text-slate-400 mt-4">South Africa time (SAST)</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {DAY_NAMES.map((name, d) => (
                  <button key={d} onClick={() => toggleDay(d)} data-testid={`office-day-${d}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${hours.days.includes(d)
                      ? 'bg-[#0f2b4a] text-white border-[#0f2b4a]' : 'bg-white text-slate-500 border-slate-200'}`}>
                    {name}
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Away message (shown to clients outside office hours)</Label>
                <Textarea rows={2} value={hours.away_message} onChange={e => setHours({ ...hours, away_message: e.target.value })}
                  className="mt-1 text-sm" data-testid="away-message-textarea" />
              </div>
            </>
          )}
        </div>

        <Button onClick={save} disabled={saving} data-testid="chat-settings-save-btn">
          {saving ? 'Saving...' : 'Save Chat Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}
